import styled from 'styled-components'
import { TRIAL_NUDGE_DAYS } from '../../constants'
import { useSetting } from '../../hooks'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { Border, Colors, Dimensions, Spacing } from '../../styles'
import { icons } from '../../styles/images'
import {
    SUBSCRIBE_CHARGE_TEXT,
    daysLeftText,
    formatTrialEndDate,
    hasManageableSubscriptionStatus,
} from '../../utils/subscription'
import Flex from '../atoms/Flex'
import { Icon } from '../atoms/Icon'
import GTButton from '../atoms/buttons/GTButton'
import { BodySmall, TitleSmall } from '../atoms/typography/Typography'
import { SubscribeButton } from './SubscriptionButtons'

const DISMISSED = 'true'
const DISMISSED_UNTIL_FINAL_DAY = 'dismissed_until_1_day_left'

export const Banner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._16};
    padding: ${Spacing._12} ${Spacing._16};
    background-color: ${Colors.background.white};
    border-bottom: ${Border.stroke.medium} solid ${Colors.background.border};
    box-sizing: border-box;

    ${Dimensions.mediaQuery.phone} {
        flex-wrap: wrap;
        align-items: flex-start;
        padding: ${Spacing._12} ${Dimensions.safeGutter(Spacing._12, 'right')} ${Spacing._12}
            ${Dimensions.safeGutter(Spacing._12, 'left')};
    }
`

export const Message = styled(Flex)`
    min-width: 0;
`

export const Actions = styled(Flex)`
    flex: 0 0 auto;

    ${Dimensions.mediaQuery.phone} {
        margin-left: auto;
    }
`

interface TrialUpgradeBannerProps {
    shouldLoadSecondaryQueries?: boolean
}

const TrialUpgradeBanner = ({ shouldLoadSecondaryQueries = true }: TrialUpgradeBannerProps) => {
    const { data: userInfo } = useGetUserInfo()
    const {
        field_value: hasDismissedTrialPrompt,
        updateSetting: setHasDismissedTrialPrompt,
        isLoading,
    } = useSetting('has_dismissed_trial_prompt', shouldLoadSecondaryQueries)

    const daysRemaining = userInfo?.trial_days_remaining ?? 0
    const hasManageableSubscription = hasManageableSubscriptionStatus(
        userInfo?.subscription_status,
        userInfo?.is_subscribed
    )
    const shouldNudge =
        userInfo?.is_in_trial && !hasManageableSubscription && daysRemaining > 0 && daysRemaining <= TRIAL_NUDGE_DAYS
    const isDismissed =
        hasDismissedTrialPrompt === DISMISSED ||
        (hasDismissedTrialPrompt === DISMISSED_UNTIL_FINAL_DAY && daysRemaining > 1)

    if (isLoading || !shouldNudge || isDismissed) return null

    const trialEndDate = formatTrialEndDate(userInfo?.trial_ends_at)

    const handleDismiss = () => {
        setHasDismissedTrialPrompt(daysRemaining > 1 ? DISMISSED_UNTIL_FINAL_DAY : DISMISSED)
    }

    return (
        <Banner>
            <Message gap={Spacing._8} alignItems="center">
                <Icon icon={icons.timer} color="gray" />
                <Flex column>
                    <TitleSmall>{`Free trial · ${daysLeftText(daysRemaining)}`}</TitleSmall>
                    <BodySmall color="light">
                        {trialEndDate ? `Ends ${trialEndDate} · ${SUBSCRIBE_CHARGE_TEXT}` : SUBSCRIBE_CHARGE_TEXT}
                    </BodySmall>
                </Flex>
            </Message>
            <Actions gap={Spacing._8} alignItems="center">
                <SubscribeButton />
                <GTButton icon={icons.x} tooltipText="Dismiss" onClick={handleDismiss} styleType="icon" />
            </Actions>
        </Banner>
    )
}

export default TrialUpgradeBanner
